import * as moment from 'moment-timezone';
import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { Challenge, ChallengeService, TeamUser } from 'app/shared/services/challenge.service';
import { Subject } from 'rxjs';
import { User } from 'app/shared/models/User';
import { MatDialog } from '@angular/material';
import { ExerciseService } from 'app/shared/services/exercises.service';
import { Module } from 'app/shared/models/Module';
import { VideoDialogComponent } from 'app/theme/components/videoDialog/videoDialog.component';

/**
 * Component to displayed leaderboard of a challenge
 */
@Component({
  selector: 'app-challenge-leaderboard-bonus',
  templateUrl: './challenge-leaderboard-bonus.component.html',
  styleUrls: ['./challenge-leaderboard-bonus.component.scss'],
})
export class ChallengeLeaderboardBonusComponent implements OnDestroy, OnChanges, OnInit {
  // The displayed challenge
  @Input() challenge: Challenge;

  // The currently logged in user
  @Input() currentUser: User;

  // The currently logged in user object in team
  @Input() teamUser: TeamUser;

  // Current day of challenge (Required to check if daily bonus was already granted for today)
  @Input() currentChallengeDay: number;

  // The freshen up module
  public freshenUpModule: Module;

  // The relaxer module (Tea time bonus can be gained through exercises which are part of relaxer)
  public relaxerModule: Module;

  // Whether tea time bonus of current day is available for user
  public teaTimeBonusAvailable: boolean;

  // Whether freshen up bonus of current day is available for user
  public freshenUpBonusAvailable: boolean;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(private challengeService: ChallengeService, private exerciseService: ExerciseService, private dialog: MatDialog) {}

  public ngOnInit() {
    // Fetch freshen up module in order to allow opening freshen up video dialog by clicking on bonus
    this.exerciseService.getModules().then((modules) => {
      this.freshenUpModule = modules.find((mod) => mod.title.de === 'Frischmacher' || mod._id === '5a9db3d00db3c60000ad6fef');
      this.relaxerModule = modules.find((mod) => mod.title.de === 'Ausgleicher' || mod._id === '5a9db5ae0db3c60000ad6ff2');
      this.updateBonusAvailability();
    });
  }

  public ngOnChanges(): void {
    this.updateBonusAvailability();
  }

  /**
   * Start freshenUp module or relaxer module by clicking on bonus
   * @param module
   */
  public openVideoDialog(module: Module) {
    let dialogOpened;

    this.exerciseService.createRandomProgram(module, undefined).then((program) => {
      dialogOpened = moment();
      const dialogRef = this.dialog.open(VideoDialogComponent, {
        panelClass: 'app-video-dialog-container',
        // width: '60vw',
        position: {
          top: '5vh',
        },
        data: { program, canGoBack: false },
      });

      dialogRef
        .afterClosed()
        .takeUntil(this.ngUnsubscribe)
        .subscribe(async (result) => {
          // If at least 15 seconds passed, mark module as finished
          if (moment().diff(dialogOpened, 'seconds') >= 15) {
            // Send request to backend to indicate that user completed a module
            this.exerciseService.moduleFinished(module._id).then(() => {
              this.challengeService.reloadTeams();
            });
          }
        });
    });
  }

  /**
   * On destroy cancel active subscriptions
   */
  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Update whether tea time and freshenUp bonus are available
   */
  private updateBonusAvailability() {
    if (!this.currentUser) {
      return;
    }

    if (!this.isWorkday()) {
      this.teaTimeBonusAvailable = false;
      this.freshenUpBonusAvailable = false;
      return;
    }

    const currentUserTime = moment().tz(this.currentUser.timeZone);

    if (this.teamUser.bonuses.teatime.days.includes(this.currentChallengeDay)) {
      this.teaTimeBonusAvailable = false;
    } else if (currentUserTime.hours() < 15) {
      this.teaTimeBonusAvailable = false;
    } else {
      this.teaTimeBonusAvailable = true;
    }

    if (this.teamUser.bonuses.freshenUp.days.includes(this.currentChallengeDay)) {
      this.freshenUpBonusAvailable = false;
    } else if (currentUserTime.hours() < 11 || (currentUserTime.hours() === 11 && currentUserTime.minutes() < 30)) {
      this.freshenUpBonusAvailable = true;
    } else {
      this.freshenUpBonusAvailable = false;
    }
  }

  /**
   * Check whether now is a workday in users timezone
   * @param timeZone - timeZone
   */
  private isWorkday() {
    if (!this.currentUser) {
      return;
    }

    const day = moment().tz(this.currentUser.timeZone).day();
    if (day > 0 && day < 6) {
      return true;
    }
    return false;
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src1521087412/src/client/web-client/src/app/theme/components/challenge-statistics/components/challenge-leaderboard/components/challenge-leaderboard-bonus/challenge-leaderboard-bonus.component.ts